import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { organization } from '@trashlab/api/src/domains/identity/schemas/organization';
import { workflow_kind } from '@trashlab/api/src/domains/core/schemas/base-template';
import {
  foreignKey,
  index,
  pgTable,
  text,
  varchar,
  integer,
  uniqueIndex,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';

/**
 * Custom order type configurations.
 * Allows tenants to define custom order types with custom display names and colors.
 * The baseWorkflowKind determines which system behavior the custom type inherits.
 */
export const order_type_config = pgTable(
  'order_type_config',
  {
    ...defaultFields.fieldsWithTenantId(),

    /** Display name shown in UI (e.g., 'Bulky Item Pick Up') */
    displayName: text('displayName').notNull(),

    /** Hex color code for display (e.g., '#7950F2') */
    displayColor: varchar('displayColor', { length: 7 }).notNull(),

    /** Optional description of this order type */
    description: text('description'),

    /** Sort order for display in lists */
    sortOrder: integer('sortOrder').default(0).notNull(),

    /**
     * The system workflow kind this custom type is based on.
     * Determines the underlying behavior (routing, completion flow, etc.)
     */
    baseWorkflowKind: workflow_kind('baseWorkflowKind').notNull(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('order_type_config_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      updatedAtTs_idx: index('order_type_config_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_desc_idx: index('order_type_config_updatedAtTs_desc_idx').on(
        table.updatedAtTs.desc(),
      ),
      tenant_archived_idx: index('order_type_config_tenant_archived_idx').on(
        table.tenantId,
        table.archivedAtTs,
      ),

      // Partial unique index: displayName must be unique per tenant for non-archived configs
      tenant_displayName_active_unique: uniqueIndex(
        'order_type_config_tenant_displayName_active_unique',
      )
        .on(table.tenantId, table.displayName)
        .where(sql`${table.archivedAtTs} IS NULL`),

      // Foreign keys
      fk_order_type_config_tenant: foreignKey({
        columns: [table.tenantId],
        foreignColumns: [organization.id],
        name: 'fk_order_type_config_tenant',
      }),
    };
  },
);

/**
 * History table for order_type_config
 */
export const order_type_config_history = pgTable(
  'order_type_config_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    displayName: text('displayName'),
    displayColor: varchar('displayColor', { length: 7 }),
    description: text('description'),
    sortOrder: integer('sortOrder'),
    baseWorkflowKind: workflow_kind('baseWorkflowKind'),
  },
  (table) => {
    return {
      id_version_number_idx: index(
        'order_type_config_history_id_version_number_idx',
      ).on(table.id, table.versionNumber),
    };
  },
);

export type OrderTypeConfigSchema = typeof order_type_config.$inferSelect;
export type OrderTypeConfigInsert = typeof order_type_config.$inferInsert;
